import React, { useRef, useEffect, useCallback } from "react";
import heartLoading from "../../assests/heartLoading.gif";
import AssetTableHeader from "../../component/DataMonitor/AssetTableHeader";
import AssetTableHead from "../../component/DataMonitor/AssetTableHead";
import AssetTableBody from "../../component/DataMonitor/AssetTableBody";

const AssetTable = ({
  tableData = [],
  loading = false,
  assetCount = 0,
  error = null,
  totalRecords = 0,
  loadMore = () => {},
  hasMore = false,
  isFetchingMore = false,
  onSortChange = () => {},
  onReset = () => {},
  onColumnSearchChange = () => {},
  onSearchSubmit = () => {},
  columnSearch = {},
}) => {
  const items = [
    { color: "#FB0301", label: "< 90%" },
    { color: "#FFC100", label: "90% – 95%" },
    { color: "#009F52", label: "≥ 95%" },
  ];
  const tableContainerRef = useRef(null);
  const hasMoreRef = useRef(hasMore);
  const isFetchingMoreRef = useRef(isFetchingMore);
  const [sortColumn, setSortColumn] = React.useState(null);
  const [sortOrder, setSortOrder] = React.useState("asc");

  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { isFetchingMoreRef.current = isFetchingMore; }, [isFetchingMore]);

  const handleResetInputs = () => {
    if (typeof onColumnSearchChange === "function") {
      Object.keys(columnSearch).forEach((col) => onColumnSearchChange(col, ""));
    }
    onReset();
  };

  const handleSort = (column) => {
    let newOrder = "asc";
    if (sortColumn === column && sortOrder === "asc") {
      newOrder = "desc";
    }
    setSortColumn(column);
    setSortOrder(newOrder);
    if (typeof onSortChange === "function") {
      onSortChange(column, newOrder);
    }
  };

  const handleScroll = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (
      scrollTop + clientHeight >= scrollHeight * 0.5 &&
      hasMoreRef.current &&
      !isFetchingMoreRef.current
    ) {
      loadMore();
    }
  }, [loadMore]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const renderHighlightedText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    const parts = text.split(new RegExp(`(${searchTerm})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={i} style={{ backgroundColor: "orange", fontWeight: "500" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  if (error) {
    return <div className="p-4 text-red-600">Error: {error}</div>;
  }

  return (
    <div className="poppins-regular">
      <AssetTableHeader
        items={items}
        assetCount={assetCount}
        totalRecords={totalRecords}
        loadedCount={tableData.length}
        handleResetInputs={handleResetInputs}
        tableContainerRef={tableContainerRef}
      />
      <div
        ref={tableContainerRef}
        style={{ overflowX: "auto", overflowY: "auto", maxHeight: "600px", scrollBehavior: "smooth" }}
      >
        <table className="min-w-full border-collapse border border-gray-200 text-sm">
          <AssetTableHead
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            handleSort={handleSort}
            columnSearch={columnSearch}
            onColumnSearchChange={onColumnSearchChange}
            onSearchSubmit={onSearchSubmit}
          />
          <AssetTableBody
            tableData={tableData}
            loading={loading}
            columnSearch={columnSearch}
            renderHighlightedText={renderHighlightedText}
          />
        </table>
        
      </div>

      {isFetchingMore && (
        <div className="flex items-center justify-center py-6 gap-2">
          <img src={heartLoading} alt="Loading more..." className="h-32 w-32" />
          <span className="text-gray-500 text-2xl">Loading more...</span>
        </div>
      )}
      {!hasMore && tableData.length > 0 && (
        <div className="text-center py-3 text-gray-400 text-xs">
          All {totalRecords} records loaded
        </div>
      )}
    </div>
  );
};

export default AssetTable;
